import React, { useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";

import { readState } from "@/__main__/app-state.mjs";
import { setRoute } from "@/__main__/router.mjs";
import { hardCodeURLs } from "@/app/constants.mjs";
import { removeBlitzUserAccount } from "@/feature-auth/fetches/remove-blitz-user.mjs";
import * as AuthActions from "@/feature-auth/utils/auth-actions.mjs";
import { isUserSubscriber } from "@/feature-subscriber/utils/subscriber.mjs";
import { useActiveSubscriptions } from "@/feature-wallet/utils/subscription-status.mjs";
import DeleteUserIcon from "@/inline-assets/settings-delete-user.svg";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogWindow,
} from "@/shared/Dialog.jsx";
import { allGameProfiles } from "@/util/all-game-profiles.mjs";
import { devWarn } from "@/util/dev.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const DeleteAccountModalBody = styled(DialogBody)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);

  padding-block: var(--dialog-padding);

  .delete-icon {
    padding-bottom: var(--sp-4);
  }

  .body-text {
    color: var(--shade2);
    line-height: var(--sp-6);
    text-align: center;
  }

  .icons {
    display: flex;
    flex-direction: row;
    gap: var(--sp-3);

    margin-top: calc(var(--dialog-padding) / 2);

    .profile-icon {
      width: var(--sp-8);
      height: var(--sp-8);
      border-radius: 50%;
      object-fit: cover;
    }
  }
`;

const DeleteAccountModal = ({ close }) => {
  const state = useSnapshot(readState);
  const gameSymbol = useGameSymbol();
  const {
    settings: { loggedInAccounts },
    tft,
    user,
  } = state;

  const activeSubscriptions = useActiveSubscriptions();

  const hasActiveSubscription =
    activeSubscriptions.length || isUserSubscriber(user?.roles);

  const allProfiles = useMemo(
    () => allGameProfiles(gameSymbol, loggedInAccounts, tft),
    [gameSymbol, loggedInAccounts, tft],
  );

  const deleteAccount = useCallback(async () => {
    try {
      await removeBlitzUserAccount();
    } catch (err) {
      devWarn(err);
    } finally {
      close();
      await AuthActions.logoutUser();
      setRoute("/account");
    }
  }, [close]);

  const { t } = useTranslation();

  return (
    <DialogWindow width={414}>
      <DialogContent>
        <DeleteAccountModalBody>
          <div className="delete-icon">
            <DeleteUserIcon />
          </div>

          <div className="type-h6">
            {!hasActiveSubscription
              ? t(
                  "common:settings.deleteBlitzAccount.title",
                  "Delete Blitz Account",
                )
              : t(
                  "common:settings.deleteBlitzAccount.activeSubscription.title",
                  "Ongoing Pro Subscription",
                )}
          </div>

          <div className="type-body2 body-text">
            {!hasActiveSubscription ? (
              t(
                "common:settings.deleteBlitzAccount.body",
                "Are you sure you want to delete your account? You will lose access to all Blitz features for your profiles:",
              )
            ) : (
              <Trans i18nKey="common:settings.deleteBlitzAccount.activeSubscription.body">
                You have an ongoing Premium subscription. You can delete your
                account after your subscription expired.
                <br />
                If you think that there is a problem, feel free to contact our{" "}
                <a
                  href={hardCodeURLs.BLITZ_FEEDBACK}
                  style={{ color: "var(--primary)" }}
                >
                  support
                </a>
                .
              </Trans>
            )}
          </div>

          {!hasActiveSubscription ? (
            <div className="icons">
              {allProfiles?.length > 0 &&
                allProfiles.map((account) => (
                  <img
                    key={account?.url}
                    className="profile-icon"
                    src={account?.imgUrl || ""}
                    alt={account?.name}
                  />
                ))}
            </div>
          ) : null}
        </DeleteAccountModalBody>

        <DialogFooter>
          <Button className="button" size="large" block onClick={close}>
            {t("common:cancel", "Cancel")}
          </Button>

          {!hasActiveSubscription ? (
            <Button
              className="button"
              emphasis="high"
              size="large"
              block
              onClick={deleteAccount}
            >
              {t("common:yes", "Yes")}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </DialogWindow>
  );
};

export default DeleteAccountModal;
